import React from 'react';
import PropTypes from 'prop-types';

import { AdvancedImage, responsive } from '@cloudinary/react';
import { Cloudinary, Actions } from '@cloudinary/url-gen';

import MediaGallery from './MediaGallery';
import PressContacts from './PressContacts';
import SocialIcons from '../../../common/SocialIcons';
import TitleHeader from '../../common/PressReleaseWrittenContent/TitleHeader';

import { convertTimeStampToEtDateTime } from '../helpers/timeHelpers';
import PRESS_RELEASE_STATUS from '../helpers/pressReleaseUtils';

const PressReleaseWrittenContentV2 = ({
  pressRelease,
  cloudinaryCloudName,
}) => {
  const cloudinary = new Cloudinary({ cloud: { cloudName: cloudinaryCloudName } });
  const logoImage = cloudinary.image(pressRelease.logo_path);
  const headerImage = cloudinary.image(pressRelease.header_image_path);
  logoImage.delivery(Actions.Delivery.dpr(window.devicePixelRatio ?? 1));
  headerImage.delivery(Actions.Delivery.dpr(window.devicePixelRatio ?? 1));

  const displayFullText = () => {
    return { __html: pressRelease.full_text };
  };
  const displayCompanyDescription = () => {
    return { __html: pressRelease.company_description };
  };

  let formattedPublishDate;
  let changesReflectedDateString;
  const today = new Date();
  const todayFormatted = today.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  if (pressRelease.publish_date) {
    const publishDate = convertTimeStampToEtDateTime(pressRelease.publish_date);
    formattedPublishDate = publishDate.toFormat('MMMM d, yyyy');

    // If publish date is in the future, changes will be reflected on that date
    // If publish date is in the past or today, changes are reflected today
    if (publishDate.toJSDate() > today) {
      changesReflectedDateString = `on ${formattedPublishDate}`;
    } else {
      changesReflectedDateString = `on ${todayFormatted}`;
    }
  } else {
    // No publish date - both dates are today
    formattedPublishDate = todayFormatted;
    changesReflectedDateString = 'immediately';
  }
  const warningText = pressRelease?.status === PRESS_RELEASE_STATUS.PAID
    ? `Your changes have been saved and will be published ${changesReflectedDateString}.`
    : 'This is a preview and has not yet been purchased or published.';

  return (
    <>
      <div className="written-content-react">
        <TitleHeader
          title={pressRelease.title}
          subheader={pressRelease.subheader}
          companyName={pressRelease.company_name}
          companyUrl={pressRelease.company_url}
          hasLogo={pressRelease.cloudinary_company_logo !== ''}
          logoImage={logoImage}
          warningText={warningText}
        />
        <article className="content">
          <div className="row">
            <div className="large-4 medium-4 columns">
              <div className="content__publish-date no-print">{formattedPublishDate}</div>
              <div className="content__contacts-divider no-print" />
              <SocialIcons />
              {/* eslint-disable-next-line camelcase, max-len */}
              {pressRelease.press_contacts.length > 0 && <PressContacts pressContacts={pressRelease.press_contacts} />}
            </div>
            <div className="large-7 medium-8 end columns">
              {pressRelease.cloudinary_header_image !== '' && (
                <div className="content__image__wrapper no-print">
                  <AdvancedImage cldImg={headerImage} className="content__image" plugins={[responsive({ steps: [400, 800] })]} />
                </div>
              )}
              <div className="content__text">
                {/*
                  The pressrelease.full_text is raw HTML so we need to insert it into
                  the DOM using dangerouslySetInnerHTML
                */}
                {/* eslint-disable-next-line react/no-danger */}
                {pressRelease.ap_full_location && (
                  <span className="content__text__location">
                    <strong>{pressRelease.ap_full_location} — </strong>
                  </span>
                )}
                {/* eslint-disable-next-line react/no-danger */}
                <span className="content__text__full-text" dangerouslySetInnerHTML={displayFullText()} />
              </div>
              {pressRelease.company_description !== '' && (
                <div className="content__company-boilerplate">
                  <div className="content__company-boilerplate__hash">###</div>
                  <div className="content__company-boilerplate__header">About</div>
                  {/*
                  The pressrelease.company_description is raw HTML so we need to insert it into
                  the DOM using dangerouslySetInnerHTML
                  */}
                  {/* eslint-disable-next-line react/no-danger */}
                  <div className="content__company-boilerplate__text" dangerouslySetInnerHTML={displayCompanyDescription()} />
                </div>
              )}
              {pressRelease.multimedia_add_ons.length > 0 && (
                // eslint-disable-next-line max-len
                <MediaGallery cloudinaryCloudName={cloudinaryCloudName} multimediaAddOns={pressRelease.multimedia_add_ons} />
              )}
              <div className="content__divider no-print" />
              <SocialIcons />
            </div>
          </div>
        </article>
      </div>
    </>
  );
};

PressReleaseWrittenContentV2.propTypes = {
  pressRelease: PropTypes.shape({
    id: PropTypes.number,
    ap_full_location: PropTypes.string,
    billing_contact_email: PropTypes.string,
    billing_contact_name: PropTypes.string,
    checkout_token: PropTypes.string,
    cloudinary_company_logo: PropTypes.string,
    cloudinary_header_image: PropTypes.string,
    company_city: PropTypes.string,
    company_country: PropTypes.string,
    company_description: PropTypes.string,
    company_name: PropTypes.string,
    company_state_territory: PropTypes.string,
    company_url: PropTypes.string,
    coupon_code_id: PropTypes.number,
    crosspost_sites: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      }),
    ),
    full_text: PropTypes.string,
    header_image_path: PropTypes.string,
    logo_path: PropTypes.string,
    multimedia_add_ons: PropTypes.arrayOf(
      PropTypes.shape({
        cloudinary_id: PropTypes.number,
        cloudinary_file: PropTypes.string,
      }),
    ),
    newsletter_add_ons: PropTypes.arrayOf(
      PropTypes.shape({
        site_id: PropTypes.number,
        newsletter_schedule_id: PropTypes.number,
      }),
    ),
    orig_summary: PropTypes.string,
    payment_intent_id: PropTypes.string,
    press_contacts: PropTypes.arrayOf(
      PropTypes.shape({
        email: PropTypes.string,
        name: PropTypes.string,
        phone_number: PropTypes.string,
      }),
    ),
    publish_date: PropTypes.string,
    source_link: PropTypes.string,
    status: PropTypes.string,
    subheader: PropTypes.string,
    submitted_date: PropTypes.string,
    submitter_company: PropTypes.string,
    submitter_email: PropTypes.string,
    submitter_name: PropTypes.string,
    submitter_title: PropTypes.string,
    title: PropTypes.string,
  }).isRequired,
  cloudinaryCloudName: PropTypes.string.isRequired,
};

export default PressReleaseWrittenContentV2;
